import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Home } from 'lucide-react';
import { CHILD_COPY, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Avatar, Button, Card, Chip, ErrorNote, Input, SectionTitle, Spinner } from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { uploadMediaAsset } from '@/lib/upload';
import { formatFullDate, formatPhone } from '@/lib/format';
import { useCanWrite, useRole } from '@/store/auth';
import type { ChildSummary } from '@/types';

interface ChildDetail extends ChildSummary {
  sex: string | null;
  bloodType: string | null;
  notes: string | null;
  address: string | null;
  emergencyContact?: { name?: string; relation?: string; phone?: string } | null;
  correctedDueDate: string | null;
  photoUrl?: string | null;
  guardians: {
    id: string;
    relation: string;
    isPrimary: boolean;
    user: { id: string; name: string; phone: string | null };
  }[];
}

const RELATION_LABEL: Record<string, string> = {
  MOTHER: '어머니',
  FATHER: '아버지',
  GRANDPARENT: '조부모',
  OTHER: '기타',
};

/** 원아 프로필 — 명세서 §5.2 (열람 시 감사 로그가 남는다, §9.1) */
export function ChildProfilePage() {
  const { id = '' } = useParams();
  const role = useRole();
  const isStaff = role === ROLES.DIRECTOR || role === ROLES.TEACHER;

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['children', id],
    queryFn: () => apiData<ChildDetail>(`/children/${id}`),
  });

  if (isPending) return <Spinner />;
  if (isError || !data) {
    return (
      <Screen withNav={false}>
        <AppBar title="원아 정보" back />
        <ErrorNote message="원아 정보를 불러오지 못했어요." onRetry={() => void refetch()} />
      </Screen>
    );
  }

  return (
    <Screen withNav={false}>
      <AppBar title={data.name} back />

      <div className="flex flex-col gap-4 p-4">
        <Card className="flex items-center gap-4">
          <Avatar name={data.name} size={64} photoUrl={data.photoUrl} />
          <div className="min-w-0 flex-1">
            <p className="text-xl font-bold">{data.name}</p>
            <p className="text-base text-ink-sub">
              {data.age.label}
              {data.age.corrected ? ' (교정월령)' : ''}
              {data.sex ? ` · ${data.sex}` : ''}
            </p>
            <p className="text-sm text-ink-sub">{formatFullDate(data.birthDate)}</p>
          </div>
          {isStaff && <PhotoUploadButton childId={data.id} hasPhoto={Boolean(data.photoUrl)} />}
        </Card>

        {data.currentPeriod && (
          <Card>
            <p className="text-sm font-semibold text-ink">현재 발달 구간</p>
            <p className="mt-1 text-base">{data.currentPeriod.label}</p>
            {isStaff && (
              <Link to={`/records/development/${data.id}`}>
                <Button variant="outline" block className="mt-3">
                  발달기록 작성하기
                </Button>
              </Link>
            )}
          </Card>
        )}

        {/* 기본 정보 (§5.2 v1.22) — 주소는 원과 보호자가 함께 고친다 */}
        <section>
          <SectionTitle>기본 정보</SectionTitle>
          <Card className="flex flex-col gap-3">
            <AddressRow childId={data.id} address={data.address} />
            {data.emergencyContact?.name && (
              <p className="text-sm">
                <span className="text-ink-sub">{CHILD_COPY.emergencyLabel}</span> · {data.emergencyContact.name}
                {data.emergencyContact.relation ? `(${data.emergencyContact.relation})` : ''}
                {data.emergencyContact.phone ? ` ${formatPhone(data.emergencyContact.phone)}` : ''}
              </p>
            )}
            {data.bloodType && (
              <p className="text-sm">
                <span className="text-ink-sub">혈액형</span> · {data.bloodType}
              </p>
            )}
          </Card>
        </section>

        {(data.allergies?.length ?? 0) > 0 && (
          <section>
            <SectionTitle>알레르기·특이사항</SectionTitle>
            <Card>
              <div className="flex flex-wrap gap-1.5">
                {data.allergies?.map((item) => (
                  <Chip key={item} tone="secondary">
                    {item}
                  </Chip>
                ))}
              </div>
              {data.notes && <p className="mt-3 text-base text-ink">{data.notes}</p>}
            </Card>
          </section>
        )}

        {isStaff && data.guardians.length > 0 && (
          <section>
            <SectionTitle>보호자</SectionTitle>
            <ul className="flex flex-col gap-2">
              {data.guardians.map((guardian) => (
                <Card key={guardian.id} as="li" className="flex items-center gap-3">
                  <Avatar name={guardian.user.name} size={40} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {guardian.user.name}
                      {guardian.isPrimary && (
                        <span className="ml-1.5 text-sm text-primary-strong">대표</span>
                      )}
                    </p>
                    <p className="truncate text-sm text-ink-sub">
                      {RELATION_LABEL[guardian.relation] ?? guardian.relation}
                      {guardian.user.phone ? ` · ${formatPhone(guardian.user.phone)}` : ''}
                    </p>
                  </div>
                </Card>
              ))}
            </ul>
          </section>
        )}

        <Link to={`/scope?childId=${data.id}`}>
          <Button variant="outline" block>
            성장 기록 보기
          </Button>
        </Link>
      </div>
    </Screen>
  );
}

/**
 * 원아 사진 등록·변경 (§5.2) — 교직원 전용.
 * 올린 사진은 프로필과 생활기록부(§5.13 인적사항 사진 칸)에 쓰인다.
 */
function PhotoUploadButton({ childId, hasPhoto }: { childId: string; hasPhoto: boolean }) {
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { key } = await uploadMediaAsset(file);
      await api(`/children/${childId}`, { method: 'PATCH', body: { photoKey: key } });
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['children'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '사진을 올리지 못했어요.'),
  });

  if (!canWrite) return null;

  return (
    <div className="flex shrink-0 flex-col items-end gap-1">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) upload.mutate(file);
        }}
      />
      <Button
        size="sm"
        variant="outline"
        icon={<Camera size={14} />}
        loading={upload.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {hasPhoto ? '사진 변경' : '사진 등록'}
      </Button>
      {error && <p className="text-right text-xs text-notice">{error}</p>}
    </div>
  );
}

/** 주소 한 줄 — 보기와 고치기를 같은 자리에서. 교직원과 보호자 모두 고칠 수 있다 (§5.2 v1.22) */
function AddressRow({ childId, address }: { childId: string; address: string | null }) {
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(address ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api(`/children/${childId}/address`, { method: 'PATCH', body: { address: value.trim() || null } }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['children'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '주소를 저장하지 못했어요.'),
  });

  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={CHILD_COPY.addressPlaceholder}
          autoFocus
        />
        <p className="text-sm text-ink-sub">{CHILD_COPY.addressHint}</p>
        {error && <p className="text-sm text-notice">{error}</p>}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setEditing(false); setValue(address ?? ''); }}>
            취소
          </Button>
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
            저장
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2">
      <Home size={16} className="mt-0.5 shrink-0 text-ink-sub" />
      <p className="min-w-0 flex-1 text-sm">
        <span className="text-ink-sub">{CHILD_COPY.addressLabel}</span> ·{' '}
        {address ?? <span className="text-ink-sub">{CHILD_COPY.addressEmpty}</span>}
      </p>
      {canWrite && (
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          {address ? '수정' : '입력'}
        </Button>
      )}
    </div>
  );
}
